import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import communityService, { MabarParticipant } from '@/services/communityService';
import badmintonService, { BadmintonSessionDetail } from '@/services/badmintonService';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import Notification from '@/components/ui/Notification';
import BadmintonRoundCard from '@/components/community/badminton/BadmintonRoundCard';
import {
  countCompletedRounds,
  formatBadmintonSessionStatus,
  formatPlayerFormat,
  formatSessionScheduleLabel,
} from '@/components/community/badminton/badmintonUiUtils';
import { isCustomerAppPath } from '@/utils/clientPaths';

export default function BadmintonSessionDetailPage() {
  const { slug, sessionSlug } = useParams<{ slug: string; sessionSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isClientView = isCustomerAppPath(location.pathname);
  const isRekapRoute = location.pathname.endsWith('/rekap');
  const focusedRound = useMemo(() => {
    const raw = searchParams.get('round');
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }, [searchParams]);

  const [session, setSession] = useState<BadmintonSessionDetail | null>(null);
  const [participants, setParticipants] = useState<MabarParticipant[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    message: string;
    type: 'success' | 'error';
  }>({ isOpen: false, message: '', type: 'success' });

  const notify = (message: string, type: 'success' | 'error') =>
    setNotification({ isOpen: true, message, type });

  const loadData = useCallback(async () => {
    if (!slug || !sessionSlug) return;
    setLoading(true);
    try {
      const [detail, access] = await Promise.all([
        badmintonService.getSessionDetail(sessionSlug),
        badmintonService.getAccess(slug),
      ]);
      setSession(detail);
      setCanEdit(access.canEdit);
      if (!isClientView && access.canEdit) {
        const partRes = await communityService.getEventParticipants(slug, {
          status: 'confirmed',
          limit: 200,
        });
        setParticipants(partRes.participants ?? []);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Gagal memuat data';
      notify(message, 'error');
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [slug, sessionSlug, isClientView]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!slug) return;
    navigate(isClientView ? `/event/${slug}/badminton` : `/host/mabar/${slug}/badminton`, {
      replace: true,
    });
  }, [slug, isClientView, navigate]);

  const backUrl = isClientView
    ? `/event/${slug}/badminton`
    : `/host/mabar/${slug}/badminton`;

  const isScoreEditMode = focusedRound != null && canEdit && !isRekapRoute;
  const isEditMode = (isRekapRoute && canEdit) || isScoreEditMode;

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6">
        <Skeleton className="mb-4 h-8 w-48" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="mx-auto px-4 py-6 text-center">
        <p className="text-gray-600">Game tidak ditemukan</p>
        <ShadcnButton className="mt-4" onClick={() => navigate(backUrl)}>
          Kembali
        </ShadcnButton>
      </div>
    );
  }

  const completed = countCompletedRounds(session);
  const visibleRounds =
    focusedRound != null
      ? session.rounds.filter((round) => round.roundNumber === focusedRound)
      : session.rounds;

  const content = (
    <>
      <div className="mb-4 rounded-2xl border border-orange-100 bg-[#FFF5EE] p-5">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-xl font-bold text-gray-900">{session.title}</h1>
          <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#FF5A1F]">
            {formatBadmintonSessionStatus(session.status)}
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-600">
          {[
            formatPlayerFormat(session.playerFormat),
            formatSessionScheduleLabel(session),
            `${completed} selesai`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <p className="mt-1 text-sm font-medium text-[#FF5A1F]">
          {isScoreEditMode
            ? `Edit Skor — Ronde ${focusedRound}`
            : isEditMode
              ? 'Mode Rekap — input skor & susunan per ronde'
              : 'Detail Ronde'}
        </p>
      </div>

      <div className="space-y-4">
        {visibleRounds.length === 0 ? (
          <div className="rounded-xl border bg-white p-8 text-center text-gray-500">
            {focusedRound != null
              ? `Ronde ${focusedRound} tidak ditemukan.`
              : 'Belum ada ronde pada game ini.'}
          </div>
        ) : (
          visibleRounds.map((round) => (
            <BadmintonRoundCard
              key={round.roundNumber}
              round={round}
              canEdit={isEditMode}
              hideLineupEdit={isScoreEditMode}
              participants={participants}
              onChanged={loadData}
              onNotify={notify}
            />
          ))
        )}
      </div>
    </>
  );

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <button
        type="button"
        onClick={() => navigate(backUrl)}
        className="mb-4 flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
      >
        <Icon icon="mdi:arrow-left" className="h-4 w-4" />
        Kembali
      </button>

      <div className="block lg:hidden">{content}</div>
      <div className="hidden lg:block">{content}</div>

      {notification.isOpen && (
        <Notification
          isOpen={notification.isOpen}
          onClose={() => setNotification({ isOpen: false, message: '', type: 'success' })}
          message={notification.message}
          variant={notification.type}
        />
      )}
    </div>
  );
}
